<template>
  <b-modal 
    id="windy-weather-modal" 
    size="lg" 
    :title="modalTitle"
    hide-footer
    @hidden="onModalHidden"
  >
    <!-- Состояние загрузки -->
    <div v-if="isLoading" class="text-center p-4">
  <b-spinner class="mr-2" />
      <span>Loading weather data...</span>
    </div>

    <!-- Состояние ошибки -->
    <div v-else-if="errorMessage" class="alert alert-danger">
  <i class="fas fa-exclamation-triangle mr-2" />
      {{ errorMessage }}
    </div>

    <!-- Отображение данных о погоде -->
    <div v-else-if="weatherData" class="weather-container">
      <!-- Сводка текущей погоды -->
      <div v-if="currentWeather" class="current-weather mb-4">
        <!-- Три крупных блока в одну линию -->
        <div class="row">
          <div class="col-md-4">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-thermometer-half text-danger" />
              </div>
              <div class="weather-data">
                <div class="value">
                  {{ formatNumber(currentWeather.temp_2m, 1, '0.0') }}°C
                </div>
                <div class="label">
                  Temperature
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-wind text-primary" />
              </div>
              <div class="weather-data">
                <div class="value">
                  {{ formatNumber(currentWeather.wind_speed_10m, 1, '0.0') }} m/s
                </div>
                <div class="label">
                  Wind Speed
                </div>
                <div class="sub-label">
                  Dir {{ getWindDirection(currentWeather.wind_v_10m, currentWeather.wind_u_10m) }}°
                </div>
                <div
                  class="sub-label"
                  v-if="hasMetric(currentWeather.wind_gust) && hasMetric(currentWeather.wind_speed_10m) && currentWeather.wind_gust > currentWeather.wind_speed_10m"
                >
                  Gusts {{ formatNumber(currentWeather.wind_gust, 1) }} m/s
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4" v-if="currentWeather.waves_height && currentWeather.waves_height > 0">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-water text-blue" />
              </div>
              <div class="weather-data">
                <div class="value">
                  {{ formatNumber(currentWeather.waves_height, 1, '0.0') }} m
                </div>
                <div class="label">
                  Wave Height
                </div>
                <div class="sub-label">
                  Dir {{ Math.round(currentWeather.waves_direction || 0) }}°
                </div>
                <div class="sub-label" v-if="hasMetric(currentWeather.waves_period)">
                  Period {{ formatNumber(currentWeather.waves_period, 1) }} s
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Мелкие метрики в одну строку -->
        <div class="row mt-3">
          <div class="col-md-3">
            <div class="weather-metric">
              <i class="fas fa-tint text-info mr-2" />
              Humidity: {{ Math.round(currentWeather.rh_2m || 0) }}%
            </div>
          </div>
          
          <div class="col-md-3">
            <div class="weather-metric">
              <i class="fas fa-arrow-down text-warning mr-2" />
              Pressure: {{ Math.round(currentWeather.surface_pressure || 0) }} hPa
            </div>
          </div>
          
          <div class="col-md-3" v-if="hasMetric(currentWeather.dewpoint_2m)">
            <div class="weather-metric">
              <i class="fas fa-thermometer text-secondary mr-2" />
              Dew point: {{ formatNumber(currentWeather.dewpoint_2m, 1) }}°C
            </div>
          </div>

          <div class="col-md-3" v-if="hasMetric(currentWeather.clouds)">
            <div class="weather-metric">
              <i class="fas fa-cloud text-secondary mr-2" />
              Cloudiness: {{ Math.round(currentWeather.clouds) }}%
            </div>
          </div>
          
          <div class="col-md-3" v-if="currentWeather.rain_3h > 0">
            <div class="weather-metric">
              <i class="fas fa-cloud-rain text-blue mr-2" />
              Rain (3h): {{ currentWeather.rain_3h.toFixed(1) }} mm
            </div>
          </div>

          <div class="col-md-3" v-if="hasMetric(currentWeather.precipitation_probability)">
            <div class="weather-metric">
              <i class="fas fa-umbrella text-secondary mr-2" />
              Rain chance: {{ Math.round(currentWeather.precipitation_probability) }}%
            </div>
          </div>

          <div class="col-md-3" v-if="hasMetric(currentWeather.sea_surface_temp)">
            <div class="weather-metric">
              <i class="fas fa-water text-info mr-2" />
              Sea temp: {{ formatNumber(currentWeather.sea_surface_temp, 1) }}°C
            </div>
          </div>
        </div>

        <!-- Снег отдельно если есть -->
        <div v-if="currentWeather.snow_3h > 0" class="row mt-3">
          <div class="col-md-12">
            <div class="weather-metric">
              <i class="fas fa-snowflake text-light-blue mr-2" />
              Snow (3h): {{ currentWeather.snow_3h.toFixed(1) }} mm
            </div>
          </div>
        </div>
      </div>

      <!-- Вкладки прогноза -->
      <b-tabs card class="weather-tabs">
        <!-- Почасовой прогноз -->
        <b-tab title="24h Forecast" active>
          <div class="forecast-container">
            <div class="forecast-scroll">
              <div 
                v-for="(hour, index) in hourlyForecast" 
                :key="index" 
                class="hourly-item"
                :class="{ 'current-hour': index === 0 }"
              >
                <div class="hour-time">
                  {{ formatHourTime(hour.time) }}
                </div>
                <div class="hour-temp">
                  {{ hour.temp_2m }}°C
                </div>
                <div class="hour-wind">
                  <i class="fas fa-wind text-primary" />
                  {{ (hour.wind_speed_10m || 0).toFixed(1) }} m/s
                </div>
                <div class="hour-gust" v-if="hasMetric(hour.wind_gust) && hour.wind_gust > hour.wind_speed_10m">
                  <i class="fas fa-flag text-primary" />
                  Gust {{ hour.wind_gust.toFixed(1) }} m/s
                </div>
                <div class="hour-precip" v-if="hour.rain_1h > 0">
                  <i class="fas fa-cloud-rain text-blue" />
                  {{ hour.rain_1h.toFixed(1) }}mm
                </div>
                <div class="hour-precip-prob" v-if="hasMetric(hour.precipitation_probability)">
                  <i class="fas fa-umbrella text-secondary" />
                  {{ Math.round(hour.precipitation_probability) }}%
                </div>
                <div class="hour-waves" v-if="hasMetric(hour.waves_height) && hour.waves_height > 0">
                  <i class="fas fa-water text-blue" />
                  {{ hour.waves_height.toFixed(1) }}m
                </div>
                <div class="hour-sea" v-if="hasMetric(hour.sea_surface_temp)">
                  <i class="fas fa-temperature-high text-info" />
                  Sea {{ hour.sea_surface_temp.toFixed(1) }}°C
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Ежедневный прогноз -->
        <b-tab title="7 Day Forecast">
          <div class="daily-forecast">
            <div 
              v-for="(day, index) in dailyForecast" 
              :key="index" 
              class="daily-item"
            >
              <div class="day-name">
                {{ formatDayName(day.date) }}
              </div>
              <div class="day-temps">
                <span class="temp-max">{{ day.temp_max }}°</span>
                <span class="temp-min text-muted">{{ day.temp_min }}°</span>
              </div>
              <div class="day-wind">
                <i class="fas fa-wind text-primary" />
                {{ day.wind_avg }} m/s
              </div>
              <div class="day-precip">
                <template v-if="day.rain_sum > 0">
                  <i class="fas fa-cloud-rain text-blue" />
                  {{ day.rain_sum }}mm
                </template>
                <span v-else class="text-muted">-</span>
              </div>
              <div class="day-precip-prob">
                <template v-if="hasMetric(day.precipitation_probability)">
                  <i class="fas fa-umbrella text-secondary" />
                  {{ day.precipitation_probability }}%
                </template>
                <span v-else class="text-muted">-</span>
              </div>
              <div class="day-wave">
                <template v-if="hasMetric(day.wave_max)">
                  <i class="fas fa-water text-blue" />
                  {{ day.wave_max }}m
                </template>
                <span v-else class="text-muted">-</span>
              </div>
              <div class="day-pressure">
                <template v-if="hasMetric(day.pressure)">
                  <i class="fas fa-arrow-down text-warning" />
                  {{ day.pressure }} hPa
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- График ветра -->
        <b-tab title="Wind Chart">
          <div class="chart-container">
            <canvas ref="windChart" style="height: 300px;" />
          </div>
        </b-tab>

        <!-- График температуры -->
        <b-tab title="Temperature Chart">
          <div class="chart-container">
            <canvas ref="tempChart" style="height: 300px;" />
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </b-modal>
</template>

<script>
import Chart from 'chart.js/auto'
import dayjs from 'dayjs'

export default {
  name: 'WindyWeatherModal',
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      weatherData: null,
      coordinates: { lat: 0, lng: 0 },
      requestTime: null,
      windChart: null,
      tempChart: null
    }
  },
  computed: {
    modalTitle() {
      const coords = `${this.coordinates.lat.toFixed(3)}°, ${this.coordinates.lng.toFixed(3)}°`
      const dateTime = this.requestTime ? dayjs.unix(this.requestTime).format('DD MMM YYYY, HH:mm') : ''
      return `Weather: 💧 ${coords} 📅 ${dateTime}`
    },
    formattedDateTime() {
      if (!this.requestTime) return ''
      return dayjs.unix(this.requestTime).format('DD MMM YYYY, HH:mm')
    },
    currentWeather() {
      const series = this.weatherData
      if (!series) return null

      const timestamps = Array.isArray(series.ts) ? series.ts : []
      const hasSeries = timestamps.length > 0

      const index = hasSeries ? this.findNearestIndex(timestamps) : -1
      const current = {}

      if (index >= 0) {
        Object.keys(series).forEach(key => {
          if (key !== 'ts' && Array.isArray(series[key])) {
            current[key] = series[key][index]
          }
        })
        current.time = timestamps[index]
      }

      const metaCurrent = series.meta?.current
      if (metaCurrent) {
        if (!this.hasMetric(current.temp_2m) && this.hasMetric(metaCurrent.temperature)) {
          current.temp_2m = metaCurrent.temperature
        }
        if (!this.hasMetric(current.wind_speed_10m) && this.hasMetric(metaCurrent.windspeed)) {
          current.wind_speed_10m = metaCurrent.windspeed
        }
        if (!this.hasMetric(current.rh_2m) && this.hasMetric(metaCurrent.humidity)) {
          current.rh_2m = metaCurrent.humidity
        }
        current.isDaylight = metaCurrent.isDaylight
      }

      if (!this.hasMetric(current.wind_speed_10m) && this.hasMetric(current.wind_u_10m) && this.hasMetric(current.wind_v_10m)) {
        const speed = Math.sqrt(current.wind_u_10m ** 2 + current.wind_v_10m ** 2)
        current.wind_speed_10m = Math.round(speed * 10) / 10
      }

      return Object.keys(current).length ? current : null
    },
    hourlyForecast() {
      if (!this.weatherData?.ts) return []
      
      // Берем первые 24 часа прогноза для почасового отображения
      return this.weatherData.ts.slice(0, 24).map((timestamp, index) => {
        const forecast = { time: timestamp }
        
        Object.keys(this.weatherData).forEach(key => {
          if (key !== 'ts' && Array.isArray(this.weatherData[key])) {
            const value = this.weatherData[key][index]
            forecast[key] = value === undefined ? null : value
          }
        })
        
        return forecast
      })
    },
    dailyForecast() {
      if (!this.weatherData?.ts) return []

      const allHours = this.weatherData.ts.map((timestamp, index) => {
        const forecast = { time: timestamp }
        Object.keys(this.weatherData).forEach(key => {
          if (key !== 'ts' && Array.isArray(this.weatherData[key])) {
            const value = this.weatherData[key][index]
            forecast[key] = value === undefined ? null : value
          }
        })
        return forecast
      })

      const grouped = {}
      allHours.forEach(hour => {
        const dayKey = dayjs.unix(hour.time).format('YYYY-MM-DD')
        if (!grouped[dayKey]) {
          grouped[dayKey] = {
            date: hour.time,
            temps: [],
            winds: [],
            rains: [],
            waves: [],
            precipProbs: [],
            pressures: []
          }
        }
        grouped[dayKey].temps.push(hour.temp_2m || 0)
        grouped[dayKey].winds.push(hour.wind_speed_10m || 0)
        grouped[dayKey].rains.push(hour.rain_1h || 0)
        if (Number.isFinite(hour.waves_height)) {
          grouped[dayKey].waves.push(hour.waves_height)
        }
        if (Number.isFinite(hour.precipitation_probability)) {
          grouped[dayKey].precipProbs.push(hour.precipitation_probability)
        }
        if (Number.isFinite(hour.surface_pressure)) {
          grouped[dayKey].pressures.push(hour.surface_pressure)
        }
      })

      const base = Object.keys(grouped).slice(0, 7).map(dayKey => {
        const dayData = grouped[dayKey]
        const windAvg = dayData.winds.length
          ? (dayData.winds.reduce((a, b) => a + b, 0) / dayData.winds.length)
          : 0
        const waveMax = dayData.waves.length
          ? Math.round(Math.max(...dayData.waves) * 10) / 10
          : null
        const precipProbMax = dayData.precipProbs.length
          ? Math.round(Math.max(...dayData.precipProbs))
          : null
        const pressureAvg = dayData.pressures.length
          ? Math.round(dayData.pressures.reduce((a, b) => a + b, 0) / dayData.pressures.length)
          : null
        return {
          date: dayData.date,
          temp_max: Math.round(Math.max(...dayData.temps) * 10) / 10,
          temp_min: Math.round(Math.min(...dayData.temps) * 10) / 10,
          wind_avg: Number(windAvg.toFixed(1)),
          rain_sum: Math.round(dayData.rains.reduce((a, b) => a + b, 0) * 10) / 10,
          precipitation_probability: precipProbMax,
          wave_max: waveMax,
          pressure: pressureAvg
        }
      })

      const dailyMeta = Array.isArray(this.weatherData?.meta?.daily)
        ? this.weatherData.meta.daily
        : []

      if (!dailyMeta.length) {
        return base
      }

      const metaMap = new Map(dailyMeta.map(item => [item.label, item]))
      return base.map(entry => {
        const key = dayjs.unix(entry.date).utc().format('YYYY-MM-DD')
        const meta = metaMap.get(key)
        if (!meta) {
          return entry
        }
        return {
          ...entry,
          wind_avg: this.hasMetric(meta.wind_avg) ? Number(Number(meta.wind_avg).toFixed(1)) : entry.wind_avg,
          rain_sum: this.hasMetric(meta.rain_sum) ? Math.round(Number(meta.rain_sum) * 10) / 10 : entry.rain_sum,
          precipitation_probability: this.hasMetric(meta.precipitation_probability) ? Math.round(Number(meta.precipitation_probability)) : entry.precipitation_probability,
          wave_max: this.hasMetric(meta.wave_max) ? Math.round(Number(meta.wave_max) * 10) / 10 : entry.wave_max,
          pressure: this.hasMetric(meta.pressure) ? Math.round(Number(meta.pressure)) : entry.pressure
        }
      })
    }
  },
  methods: {
    show(coordinates, time, weatherData) {
      this.coordinates = coordinates
      this.requestTime = time
      this.weatherData = weatherData
      this.errorMessage = null
      
      this.$bvModal.show('windy-weather-modal')
      
      // Рендерим графики после показа модального окна
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderCharts()
        }, 300) // Даем время табам загрузиться
      })
    },
    
    showLoading(coordinates, time) {
      this.coordinates = coordinates
      this.requestTime = time
      this.isLoading = true
      this.weatherData = null
      this.errorMessage = null
      
      this.$bvModal.show('windy-weather-modal')
    },
    
    showError(error, coordinates, time) {
      this.coordinates = coordinates
      this.requestTime = time
      this.isLoading = false
      this.weatherData = null
      this.errorMessage = error
      
      this.$bvModal.show('windy-weather-modal')
    },
    
    updateData(weatherData) {
      this.isLoading = false
      this.weatherData = weatherData
      this.errorMessage = null
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderCharts()
        }, 300)
      })
    },
    
    onModalHidden() {
      // Очищаем графики при закрытии модала
      if (this.windChart) {
        this.windChart.destroy()
        this.windChart = null
      }
      if (this.tempChart) {
        this.tempChart.destroy()
        this.tempChart = null
      }
    },
    
    getWindDirection(v, u) {
      if (!this.hasMetric(v) || !this.hasMetric(u)) return 0
      let angle = Math.atan2(v, u) * 180 / Math.PI
      if (angle < 0) angle += 360
      return Math.round(angle)
    },
    
    formatHourTime(timestamp) {
      return dayjs.unix(timestamp).format('HH:mm')
    },
    
    formatDayName(timestamp) {
      const day = dayjs.unix(timestamp)
      const today = dayjs()
      
      if (day.isSame(today, 'day')) return 'Today'
      if (day.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow'
      
      return day.format('ddd DD')
    },

    formatNumber(value, decimals = 1, fallback = null) {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return fallback
      }
      return num.toFixed(decimals)
    },
    
    renderCharts() {
      this.renderWindChart()
      this.renderTempChart()
    },
    
    renderWindChart() {
      if (this.windChart) {
        this.windChart.destroy()
      }
      
      if (!this.$refs.windChart || !this.hourlyForecast.length) return
      
      const ctx = this.$refs.windChart.getContext('2d')
      const labels = this.hourlyForecast.map(h => this.formatHourTime(h.time))
      const windSpeeds = this.hourlyForecast.map(h => (h.wind_speed_10m || 0).toFixed(1))
      const windDirections = this.hourlyForecast.map(h => 
        this.getWindDirection(h.wind_v_10m, h.wind_u_10m)
      )
      
      this.windChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Wind Speed (m/s)',
            data: windSpeeds,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            tension: 0.4,
            yAxisID: 'y'
          }, {
            label: 'Wind Direction (°)',
            data: windDirections,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            tension: 0.4,
            yAxisID: 'y1'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Wind Speed (m/s)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Wind Direction (°)'
              },
              grid: {
                drawOnChartArea: false,
              },
            }
          }
        }
      })
    },
    
    renderTempChart() {
      if (this.tempChart) {
        this.tempChart.destroy()
      }
      
      if (!this.$refs.tempChart || !this.hourlyForecast.length) return
      
      const ctx = this.$refs.tempChart.getContext('2d')
      const labels = this.hourlyForecast.map(h => this.formatHourTime(h.time))
      const temperatures = this.hourlyForecast.map(h => h.temp_2m || 0)
      const humidity = this.hourlyForecast.map(h => h.rh_2m || 0)
      
      this.tempChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Temperature (°C)',
            data: temperatures,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            tension: 0.4,
            yAxisID: 'y'
          }, {
            label: 'Humidity (%)',
            data: humidity,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            tension: 0.4,
            yAxisID: 'y1'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Temperature (°C)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Humidity (%)'
              },
              grid: {
                drawOnChartArea: false,
              },
            }
          }
        }
      })
    },

    hasMetric(value) {
      if (value === null || value === undefined) {
        return false
      }
      const numeric = Number(value)
      return Number.isFinite(numeric)
    },

    findNearestIndex(timestamps) {
      if (!Array.isArray(timestamps) || !timestamps.length) {
        return -1
      }
      if (!this.requestTime) {
        return 0
      }
      const idx = timestamps.findIndex(ts => Number.isFinite(ts) && ts >= this.requestTime)
      if (idx === -1) {
        return timestamps.length - 1
      }
      return idx
    }
  }
}
</script>

<style scoped>
.weather-container {
  max-height: 80vh;
  overflow-y: auto;
}

.current-weather {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.weather-card {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 100%;
  min-height: 80px;
}

.weather-icon {
  font-size: 1.8rem;
  margin-right: 0.8rem;
  flex-shrink: 0;
}

.weather-data .value {
  font-size: 1.2rem;
  font-weight: 500;
  color: #495057;
}

.weather-data .label {
  font-size: 0.9rem;
  color: #6c757d;
}

.weather-data .sub-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.weather-metric {
  padding: 0.6rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 0.85rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forecast-container {
  max-height: 400px;
  overflow-y: auto;
}

.forecast-scroll {
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  overflow-x: auto;
  min-height: 120px;
}

.hourly-item {
  min-width: 80px;
  text-align: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 2px solid transparent;
}

.hourly-item.current-hour {
  background: #e3f2fd;
  border-color: #2196F3;
}

.hour-time {
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  color: #495057;
}

.hour-temp {
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.hour-wind {
  color: #1976d2;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.hour-precip {
  color: #0277bd;
  font-size: 0.8rem;
}

.hour-gust {
  color: #1976d2;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.hour-precip-prob {
  color: #6c757d;
  font-size: 0.75rem;
  margin-bottom: 0.15rem;
}

.hour-waves {
  color: #0277bd;
  font-size: 0.75rem;
  margin-bottom: 0.15rem;
}

.hour-sea {
  color: #00838f;
  font-size: 0.75rem;
  margin-bottom: 0.15rem;
}

.daily-forecast {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.daily-item {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.day-name {
  font-weight: 500;
  min-width: 80px;
  color: #495057;
}

.day-temps {
  min-width: 80px;
  text-align: center;
}

.temp-max {
  font-weight: 500;
  margin-right: 0.5rem;
  color: #495057;
}

.temp-min {
  font-size: 0.9rem;
}

.day-wind, .day-precip, .day-wave, .day-pressure {
  min-width: 80px;
  text-align: center;
  font-size: 0.9rem;
}

.day-precip-prob {
  min-width: 80px;
  text-align: center;
  font-size: 0.9rem;
}

.day-precip-prob i, .day-wave i, .day-pressure i {
  margin-right: 4px;
}

.chart-container {
  padding: 1rem;
  height: 350px;
}

:deep(.weather-tabs .nav-tabs) {
  border-bottom: 2px solid #dee2e6;
}

:deep(.weather-tabs .nav-link) {
  color: #6c757d;
  font-weight: 500;
}

:deep(.weather-tabs .nav-link.active) {
  color: #495057;
  border-bottom: 2px solid #007bff;
}

.text-blue {
  color: #0277bd !important;
}

.text-light-blue {
  color: #4fc3f7 !important;
}
</style>